/**
 * «Записи» → «Длительность» (`?group=duration`, `GET /api/appointments/duration`): сколько реально
 * длится примерка — от открытия карточки прихода в CRM до итога. Отбор — тип прихода и салон,
 * матрица «салон × тип» — по всем; разрезы — салон, мероприятие, итог, стилист, час, день недели.
 * Рядом — допущение слотов CRM: запись занимает час, «дольше часа — 50 % примерок».
 * Клик по числу — примерки за ним. Спека `docs/specs/2026-10-19-appointments-duration-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { apiFetch } from "@/lib/api";
import { NavMenu } from "@/nav/NavMenu";
import { card, td, th, Tile } from "@/pages/AppointmentsUi";

type Stat = {
  n: number;
  avg: number | null;
  median: number | null;
  p25: number | null;
  p75: number | null;
  p90: number | null;
  lt30_pct: number | null;
  gt60_pct: number | null;
  gt120_pct: number | null;
  buckets: number[];
};
type Row = Stat & { key: string; label: string };
type Duration = {
  from: string;
  to: string;
  types: number[];
  location: number | null;
  type_labels: { id: number; label: string }[];
  buckets: { label: string; from: number; to: number | null }[];
  crm_gt60_pct: number;
  total: Stat;
  skipped: Record<string, number>;
  matrix: {
    key: string;
    label: string;
    all: Stat;
    cells: Record<string, Stat>;
  }[];
  cuts: { by: string; label: string; rows: Row[] }[];
};
type Visit = {
  id: number;
  location: string;
  type: string;
  event: string;
  status: string;
  user: string;
  deal: number | null;
  zapis_at: string | null;
  start: string;
  finish: string | null;
  minutes: number;
};
type Pick = {
  by?: string | undefined;
  key?: string | undefined;
  bucket?: number | undefined;
  title: string;
};

/** Выбор типа прихода: по умолчанию первичная — она и занимает слот записи. */
export const TYPE_CHOICES: { id: string; label: string }[] = [
  { id: "1", label: "первичная" },
  { id: "2", label: "повторная" },
  { id: "6", label: "без записи" },
  { id: "3", label: "сдать платье" },
  { id: "1,2,6", label: "все примерки" },
];
const SKIP: Record<string, string> = {
  error: "статус «Ошибка»",
  not_closed: "карточку не закрыли",
  next_day: "закрыли другим днём",
};

function q(p: Record<string, string | number | null | undefined>) {
  const out = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) if (v !== undefined && v !== null && v !== "") out.set(k, String(v));
  return out.toString();
}

const мин = (m: number | null) => (m === null ? "—" : m < 120 ? `${m} мин` : `${Math.floor(m / 60)} ч ${m % 60} мин`);
const проц = (p: number | null) => (p === null ? "—" : `${p}%`);

function Count({ n, active, on }: { n: number; active: boolean; on: () => void }) {
  if (!n) return <span className="text-fg-faint">·</span>;
  return (
    <button
      type="button"
      onClick={on}
      title="Показать примерки"
      className={`tabular-nums underline decoration-border underline-offset-2 hover:decoration-fg ${
        active ? "rounded bg-accent/15 px-1 font-medium" : ""
      }`}
    >
      {n}
    </button>
  );
}

/** Распределение отбора по корзинам: длина полосы — доля, число — ссылка на примерки. */
function Spread({ d, pick, setPick }: { d: Duration; pick: Pick | null; setPick: (p: Pick) => void }) {
  const max = Math.max(1, ...d.total.buckets);
  return (
    <section className={card} data-annot="распределение длительности">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">Сколько длится примерка</h2>
        <span className="text-[12px] text-fg-faint">
          от открытия карточки прихода до итога · слот CRM отводит записи 1 час
        </span>
      </div>
      <div className="flex flex-col gap-1 px-4 py-3">
        {d.buckets.map((b, i) => {
          const n = d.total.buckets[i] ?? 0;
          const share = d.total.n ? Math.round((n * 100) / d.total.n) : 0;
          return (
            <div key={b.label} className="grid grid-cols-[6.5rem_1fr_6rem] items-center gap-3 text-[12.5px]">
              <span className={`text-right ${b.from >= 60 ? "text-fg" : "text-fg-muted"}`}>{b.label}</span>
              <div className="relative h-4 rounded-sm bg-bg">
                <div
                  className={`h-4 rounded-sm ${b.from < 30 ? "bg-warn/60" : b.from >= 120 ? "bg-err/50" : "bg-accent/50"}`}
                  style={{ width: `${(n * 100) / max}%` }}
                />
              </div>
              <span className="tabular-nums">
                <Count
                  n={n}
                  active={pick?.bucket === i && !pick.by}
                  on={() => setPick({ bucket: i, title: `${b.label} · все строки` })}
                />{" "}
                <span className="text-fg-faint">{share}%</span>
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** «Салон × тип прихода»: медиана и число примерок — вне отбора, клик ставит салон и тип. */
function Matrix({
  d,
  setFilter,
}: {
  d: Duration;
  setFilter: (types: string | undefined, location: string | undefined) => void;
}) {
  return (
    <section className={card} data-annot="салон × тип прихода">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">Салон × тип прихода</h2>
        <span className="text-[12px] text-fg-faint">медиана · в скобках — примерок; весь период, без отбора</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-1.5 text-left font-medium">Салон</th>
              {d.type_labels.map((t) => (
                <th key={t.id} className={th}>
                  {t.label}
                </th>
              ))}
              <th className={th}>дольше часа, первичная</th>
            </tr>
          </thead>
          <tbody>
            {d.matrix.map((m) => {
              const перв = m.cells["1"];
              return (
                <tr
                  key={m.key}
                  className={`border-t border-border hover:bg-bg-hover ${String(d.location) === m.key ? "bg-accent/5" : ""}`}
                >
                  <td className="px-4 py-1.5">
                    <button type="button" className="hover:underline" onClick={() => setFilter(undefined, m.key)}>
                      {m.label}
                    </button>
                  </td>
                  {d.type_labels.map((t) => {
                    const c = m.cells[String(t.id)];
                    return (
                      <td key={t.id} className={`${td} whitespace-nowrap`}>
                        {c?.n ? (
                          <button
                            type="button"
                            className="hover:underline"
                            title="отобрать салон и тип"
                            onClick={() => setFilter(String(t.id), m.key)}
                          >
                            {мин(c.median)} <span className="text-fg-faint">({c.n})</span>
                          </button>
                        ) : (
                          <span className="text-fg-faint">·</span>
                        )}
                      </td>
                    );
                  })}
                  <td className={`${td} ${(перв?.gt60_pct ?? 0) > d.crm_gt60_pct ? "text-err" : "text-fg-muted"}`}>
                    {перв?.n ? проц(перв.gt60_pct) : "·"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Cuts({
  d,
  by,
  setBy,
  pick,
  setPick,
}: {
  d: Duration;
  by: string;
  setBy: (b: string) => void;
  pick: Pick | null;
  setPick: (p: Pick) => void;
}) {
  const cut = d.cuts.find((c) => c.by === by) ?? d.cuts[0];
  if (!cut) return null;
  return (
    <section className={card} data-annot="разрез длительности">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">По разрезу</h2>
        <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Разрез">
          {d.cuts.map((c) => (
            <button
              key={c.by}
              type="button"
              role="radio"
              aria-checked={c.by === cut.by}
              onClick={() => setBy(c.by)}
              className={`rounded-full border px-2.5 py-0.5 text-[12px] ${
                c.by === cut.by ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <span className="text-[12px] text-fg-faint">в ячейках — примерок; фон — доля в строке</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-1.5 text-left font-medium">{cut.label}</th>
              <th className={th}>Примерок</th>
              <th className={th}>Медиана</th>
              <th className={th} title="половина примерок — в этих границах">
                Обычно
              </th>
              <th className={th}>&lt; 30 мин</th>
              <th className={th} title={`в слотах CRM заложено ${d.crm_gt60_pct}%`}>
                &gt; 1 ч
              </th>
              <th className={th}>&gt; 2 ч</th>
              {d.buckets.map((b) => (
                <th key={b.label} className={`${th} border-l border-border`}>
                  {b.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cut.rows.map((r) => (
              <tr key={r.key} className="border-t border-border hover:bg-bg-hover">
                <td className="max-w-[16rem] truncate px-4 py-1.5" title={r.label}>
                  {r.label}
                </td>
                <td className={`${td} font-medium`}>
                  <Count
                    n={r.n}
                    active={pick?.by === cut.by && pick.key === r.key && pick.bucket === undefined}
                    on={() =>
                      setPick({
                        by: cut.by,
                        key: r.key,
                        title: `${cut.label}: ${r.label}`,
                      })
                    }
                  />
                </td>
                <td className={`${td} whitespace-nowrap`}>{мин(r.median)}</td>
                <td className={`${td} whitespace-nowrap text-fg-muted`}>
                  {r.p25 ?? "—"}–{r.p75 ?? "—"} мин
                </td>
                <td className={`${td} ${(r.lt30_pct ?? 0) >= 25 ? "text-warn" : "text-fg-muted"}`}>
                  {проц(r.lt30_pct)}
                </td>
                <td className={`${td} ${(r.gt60_pct ?? 0) > d.crm_gt60_pct ? "text-err" : "text-fg-muted"}`}>
                  {проц(r.gt60_pct)}
                </td>
                <td className={`${td} text-fg-muted`}>{проц(r.gt120_pct)}</td>
                {r.buckets.map((n, i) => (
                  <td
                    key={d.buckets[i]?.label}
                    className={`${td} border-l border-border`}
                    style={{
                      backgroundColor: n
                        ? `color-mix(in oklab, var(--color-accent) ${Math.round((n * 60) / r.n)}%, transparent)`
                        : undefined,
                    }}
                  >
                    <Count
                      n={n}
                      active={pick?.by === cut.by && pick.key === r.key && pick.bucket === i}
                      on={() =>
                        setPick({
                          by: cut.by,
                          key: r.key,
                          bucket: i,
                          title: `${cut.label}: ${r.label} · ${d.buckets[i]?.label}`,
                        })
                      }
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Visits({ base, pick }: { base: Record<string, string | undefined>; pick: Pick }) {
  const params = q({
    ...base,
    by: pick.by,
    key: pick.key,
    bucket: pick.bucket,
  });
  const v = useQuery({
    queryKey: ["appointments-duration-visits", params],
    queryFn: () => apiFetch<{ total: number; visits: Visit[] }>(`/api/appointments/duration/visits?${params}`),
  });
  return (
    <section className={card} data-annot="примерки за числом">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">{pick.title}</h2>
        {v.data && (
          <span className="text-[12px] text-fg-faint">
            {v.data.total} примерок
            {v.data.total > v.data.visits.length ? `, показаны ${v.data.visits.length}` : ""} · от коротких к длинным
          </span>
        )}
      </div>
      {v.isPending && <p className="px-4 py-3 text-[12.5px] text-fg-muted">Загружаю…</p>}
      {v.error && <p className="px-4 py-3 text-[12.5px] text-err">{String(v.error)}</p>}
      {v.data && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-[11.5px] text-fg-faint">
              <tr>
                <th className="px-4 py-1.5 text-left font-medium">Приход</th>
                <th className="px-3 py-1.5 text-left font-medium">Начало (МСК)</th>
                <th className={th}>Минут</th>
                <th className="px-3 py-1.5 text-left font-medium">Салон</th>
                <th className="px-3 py-1.5 text-left font-medium">Тип</th>
                <th className="px-3 py-1.5 text-left font-medium">Мероприятие</th>
                <th className="px-3 py-1.5 text-left font-medium">Итог</th>
                <th className="px-3 py-1.5 text-left font-medium">Стилист</th>
                <th className="px-3 py-1.5 text-left font-medium">Записан на</th>
                <th className="px-3 py-1.5 text-left font-medium">Сделка</th>
              </tr>
            </thead>
            <tbody>
              {v.data.visits.map((x) => (
                <tr key={x.id} className="border-t border-border hover:bg-bg-hover">
                  <td className="px-4 py-1.5">
                    <NavMenu r={{ obj: "customerID", id: x.id }} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums">
                    {x.start.slice(0, 16)}–{x.finish?.slice(11, 16)}
                  </td>
                  <td className={`${td} font-medium`}>{x.minutes}</td>
                  <td className="px-3 py-1.5">{x.location}</td>
                  <td className="px-3 py-1.5">{x.type}</td>
                  <td className="px-3 py-1.5">{x.event || <span className="text-fg-faint">·</span>}</td>
                  <td className="px-3 py-1.5">{x.status}</td>
                  <td className="px-3 py-1.5">{x.user}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-fg-muted">
                    {x.zapis_at?.slice(0, 16) ?? "·"}
                  </td>
                  <td className="px-3 py-1.5 font-mono">
                    {x.deal ? (
                      <Link to={`/deals/${x.deal}`} className="underline">
                        {x.deal}
                      </Link>
                    ) : (
                      <span className="text-fg-faint">·</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function DurationView({
  from,
  to,
  types,
  location,
  by,
  set,
}: {
  from: string;
  to: string;
  types: string;
  location: string | undefined;
  by: string;
  set: (next: Record<string, string | undefined>) => void;
}) {
  const [pick, setPick] = useState<Pick | null>(null);
  const base = { from, to, types, location };
  const params = q(base);
  const r = useQuery({
    queryKey: ["appointments-duration", params],
    queryFn: () => apiFetch<Duration>(`/api/appointments/duration?${params}`),
  });
  const setFilter = (t: string | undefined, loc: string | undefined) => {
    setPick(null);
    set({ dt: t ?? (types === "1" ? undefined : types), dl: loc });
  };
  const d = r.data;
  const t = d?.total;
  const салон = d?.matrix.find((m) => m.key === location)?.label;
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-annot="отбор длительности">
        <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Тип прихода">
          {TYPE_CHOICES.map((c) => (
            <button
              key={c.id}
              type="button"
              role="radio"
              aria-checked={types === c.id}
              onClick={() => setFilter(c.id === "1" ? undefined : c.id, location)}
              className={`rounded-full border px-2.5 py-0.5 text-[12px] ${
                types === c.id ? "border-accent bg-accent/10 text-fg" : "border-border text-fg-muted hover:text-fg"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        {location && (
          <span className="flex items-center gap-1.5 rounded-full border border-accent bg-accent/10 px-2.5 py-0.5 text-[12px]">
            салон {салон ?? location}
            <button
              type="button"
              aria-label="все салоны"
              className="text-fg-muted hover:text-fg"
              onClick={() => setFilter(types, undefined)}
            >
              ×
            </button>
          </span>
        )}
      </div>
      {r.isPending && <p className="text-[12.5px] text-fg-muted">Загружаю…</p>}
      {r.error && <p className="text-[12.5px] text-err">{String(r.error)}</p>}
      {d && t && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-annot="итог длительности">
            <Tile
              label="Примерок"
              value={t.n}
              hint={
                Object.entries(d.skipped)
                  .map(([k, n]) => `${SKIP[k] ?? k} ${n}`)
                  .join(" · ") || "все закрыты в тот же день"
              }
            />
            <Tile
              label="Медиана"
              value={мин(t.median)}
              hint={`половина — от ${t.p25 ?? "—"} до ${t.p75 ?? "—"} мин · среднее ${мин(t.avg)}`}
            />
            <Tile label="Короче 30 минут" value={проц(t.lt30_pct)} hint="ушли быстро — слот свободен раньше" />
            <Tile
              label="Дольше часа"
              value={проц(t.gt60_pct)}
              hint={`в слотах CRM заложено ${d.crm_gt60_pct}% — заходят в следующий час`}
            />
            <Tile label="Дольше 2 часов" value={проц(t.gt120_pct)} hint="занимают третий час — CRM их не учитывает" />
          </div>
          <p className="text-[12px] text-fg-faint">
            Примерка — приход в CRM от открытия карточки до итога («Сделка», «Ушел думать», «Ничего не подошло»). Слоты
            CRM считают запись одним часом: в час помещается столько записей, сколько стилистов в графике (не больше
            примерочных), а из записей прошлого часа половина «ещё идёт». Длительность по салону и мероприятию CRM не
            различает — эти цифры показывают, где допущение врёт. Карточку, заведённую уже в конце примерки, отличить
            нельзя: самые короткие — проверьте списком.
          </p>
          <div className="grid gap-4 xl:grid-cols-[26rem_minmax(0,1fr)]">
            <Spread d={d} pick={pick} setPick={setPick} />
            <Matrix d={d} setFilter={setFilter} />
          </div>
          <Cuts
            d={d}
            by={by}
            setBy={(b) => set({ dby: b === "location" ? undefined : b })}
            pick={pick}
            setPick={setPick}
          />
          {pick && <Visits base={base} pick={pick} />}
        </>
      )}
    </>
  );
}
